import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import TimeSelects from 'apps/web_forms/components/commons/TimeSelects';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import getTimeZoneFormatted from 'apps/web_forms/utils/dateTimeFormatter';
import { removeTimeZoneFormatted } from 'apps/web_forms/utils/dateTimeFormatter';
import { EMPTY_DATETIME_VALUE } from 'apps/web_forms/utils/constants';

const TimeInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const { identifier, title, hint, hidden, validate_msg: validateMsg } = element;
  const { register, getValues } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const currentSelectValues = useMemo(() => {
    if (!currentValue) return {};
    const timeOnly =  currentValue.replace(/[+-]\d{4}$/, '')
    const splittedTime = timeOnly.includes("T") ? timeOnly.split('T')[1].split(':') : timeOnly.split(':');
    return {  
      hour: splittedTime[0],
      minutes: splittedTime[1],
    };
  }, [currentValue]);

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const [{ isActive, isRequired, isReadOnly, isElementValid, initialAnswer }] = useRules(element, formContext);

  const defaultValue = useMemo(() => {
    if(getValues(identifier)){
      return getValues(identifier).replace(/[+-]\d{4}$/, '');
    }
    if(initialAnswer){
      return initialAnswer.replace(/[+-]\d{4}$/, '');
    }
    return undefined;
  }, []);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    });
  }, [isElementValid, isRequired]);

  const onChange = (t, isDirty = true) => {
    if (t === EMPTY_DATETIME_VALUE) {
      console.log(identifier, "ENTER 4")
      changeValue('', isDirty);
      return;
    }
    if (t) {
      console.log(identifier,"ENTER 5")
      const timezone = getTimeZoneFormatted();
      changeValue(`${t}${timezone}`, isDirty);
    }
  };

  useEffect(() => {
    const virtualValue = getValues(identifier);

    if (virtualValue) {
      const timeOnly =  virtualValue.replace(/[+-]\d{4}$/, '')
      let time = timeOnly.includes("T") ? timeOnly.split("T")[1] : timeOnly;
      onChange(time, false);
    }else if (initialAnswer) {
      const timeOnly =  initialAnswer.replace(/[+-]\d{4}$/, '')
      let time = timeOnly.includes("T") ? timeOnly.split("T")[1] : timeOnly;
      onChange(time, false);
    } else {
      /**
       * This else is needed to trigger the Completion Circle calculations for Group/Subform
       * Ideally we don't want many of these triggers, but keep it in TODO list
      */
      onChange(EMPTY_DATETIME_VALUE, false); 
    }
  }, [initialAnswer]);

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <label htmlFor={identifier}>{title}</label>
      <TimeSelects
        identifier={identifier}
        onChange={onChange}
        isValid={errorMessage === undefined}
        hasResourceUpdateHighlight={hasResourceUpdateHighlight}
        removeResourceUpdateHighlight={removeResourceUpdateHighlight}
        readOnly={isReadOnly}
        initialValue={defaultValue}
        customHour={currentSelectValues.hour}
        customMinutes={currentSelectValues.minutes}
      />
      {hint && <p className='element-hint'>{hint}</p>}
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

TimeInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    initialAnswer: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(TimeInput);
